// 공정 표준 참여(`0110`·`0111`) — 글 고친 기록 · 숫자 제안(7일 공개 검토) · 찬반 · 운영자 결정.

import type { BaseRow } from "../../common/types";
import type { ProcessStandardItem, ProcessStandardVersion } from "../types";

export const PROPOSAL_FIELDS = ["output_per_person_day", "crew_size", "curing_days"] as const;
export type ProposalField = (typeof PROPOSAL_FIELDS)[number];
export const PROPOSAL_FIELD_LABEL: Record<ProposalField, string> = {
  output_per_person_day: "1인 1일 작업량(m²)",
  crew_size: "기능공 수",
  curing_days: "양생일",
};

export const PROPOSAL_STATUSES = ["reviewing", "approved", "rejected"] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

export const VOTE_STANCES = ["pro", "con"] as const;
export type VoteStance = (typeof VOTE_STANCES)[number];

export const PROPOSAL_REVIEW_DAYS = 7;
export const PROPOSAL_REASON_MAX = 1000;
export const ARTICLE_TITLE_MAX = 80;
export const ARTICLE_BODY_MAX = 20000;

export interface ProcessStandardArticleRevision extends BaseRow {
  article_id: string;
  org_id: string;
  org_name: string;
  title_before: string;
  body_before: string;
  title_after: string;
  body_after: string;
}

export interface ProcessStandardProposal extends BaseRow {
  item_code: string;
  base_version_id: string;
  field: ProposalField;
  current_value: number;
  proposed_value: number;
  reason: string;
  org_id: string;
  org_name: string;
  review_ends_at: string;
  status: ProposalStatus;
  decided_at: string | null;
  decided_version_id: string | null;
}

export interface ProcessStandardProposalVote extends BaseRow {
  proposal_id: string;
  org_id: string;
  org_name: string;
  stance: VoteStance;
  reason: string;
}

// 글 고치기 — 화면이 본 제목·본문(seen)이 지금과 다르면 stale.
export interface ArticleEditSave {
  revisionId: string;
  articleId: string;
  seen: { title: string; body: string };
  after: { title: string; body: string };
}

// 숫자 제안 — id·검토 마감·만든 시각은 앱이 만든다(회사명은 RPC 가 org 에서 읽는다).
export interface ProposalInsert {
  id: string;
  item_code: string;
  base_version_id: string;
  field: ProposalField;
  current_value: number;
  proposed_value: number;
  reason: string;
  review_ends_at: string;
  created_at: string;
}

export interface VoteInsert {
  id: string;
  proposal_id: string;
  stance: VoteStance;
  reason: string;
}

// 목록 상한 — 회원 제안 탭·운영자 검토 목록(Q&A 피드 한도와 같은 상수 꼴).
export const PROPOSAL_LIST_LIMIT = 200;

export interface ProposalFilter {
  status?: ProposalStatus | null;
  itemCode?: string | null;
  // 있으면 그 수만 — from 기본은 최근 것(newest) · 결과는 언제나 오래된 것부터.
  limit?: number | null;
  from?: "newest" | "oldest";
}

// 새 판 한 줄 — 판 행(번호는 RPC 가 정한다)과 항목 전부.
export type NewVersionInsert = Pick<ProcessStandardVersion, "id" | "label" | "published_at" | "note">;
export type NewItemInsert = Omit<ProcessStandardItem, "version_id" | "created_at" | "updated_at">;

export interface AdminStandardActor {
  auditId: string;
  operatorId: string;
  actorEmail: string;
}

export interface AdminStandardPublishSave extends AdminStandardActor {
  baseVersionId: string;
  version: NewVersionInsert;
  items: NewItemInsert[];
  approved: string[];
  rejected: string[];
  before: { proposal_ids: string[] };
  after: { proposal_ids: string[]; approved: string[]; rejected: string[]; version_id: string; version_label: string };
}

export interface AdminStandardRejectSave extends AdminStandardActor {
  rejected: string[];
  decidedAt: string;
  before: { proposal_ids: string[] };
  after: { proposal_ids: string[]; approved: string[]; rejected: string[]; version_id: null; version_label: null };
}

// 회원 화면 — 다른 회사 org_id 는 떼고 우리 것인지(is_mine)만.
export interface ProposalVoteView {
  id: string;
  org_name: string;
  stance: VoteStance;
  reason: string;
  created_at: string;
  is_mine: boolean;
}

export interface ProposalView {
  id: string;
  item_code: string;
  item_name: string | null;
  base_version_id: string;
  field: ProposalField;
  current_value: number;
  proposed_value: number;
  reason: string;
  org_name: string;
  is_mine: boolean;
  status: ProposalStatus;
  created_at: string;
  review_ends_at: string;
  decided_at: string | null;
  days_left: number;
  review_open: boolean;
  pro: number;
  con: number;
  my_vote: VoteStance | null;
}

export interface ProposalDetailView extends ProposalView {
  votes: ProposalVoteView[];
}

export interface ArticleRevisionView {
  id: string;
  org_name: string;
  is_mine: boolean;
  title_before: string;
  title_after: string;
  body_before: string;
  body_after: string;
  created_at: string;
}

// 운영자 화면 — 검토 끝난(또는 검토 중) 제안 한 줄과 찬반 수.
export interface AdminProposalRow {
  id: string;
  item_code: string;
  item_name: string | null;
  base_version_id: string;
  base_is_latest: boolean;
  field: ProposalField;
  current_value: number;
  proposed_value: number;
  reason: string;
  org_id: string;
  org_name: string;
  status: ProposalStatus;
  created_at: string;
  review_ends_at: string;
  review_open: boolean;
  pro: number;
  con: number;
  votes: ProposalVoteView[];
}
